'use client'

/* eslint-disable security/detect-object-injection */
// Object accesses in this file use controlled keys from validated enums

import { useState, useEffect, useRef, useCallback } from 'react'
import { 
  Brain, 
  AlertTriangle, 
  Zap,
  BarChart3,
  Settings,
  Minimize2,
  Maximize2
} from 'lucide-react'

/**
 * BIRHAUS CognitiveLoadMeter
 * 
 * Real-time cognitive load analysis tool for developers:
 * - Measures Miller's Law compliance (7±2 items)
 * - Tracks visual complexity and information density
 * - Spanish-first violation messages
 * - Performance impact monitoring
 * - Accessibility compliance checking
 * - Real-time suggestions for improvement
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

// Import central CognitiveLoadMetrics and extend it with tools-specific metrics
import type { CognitiveLoadMetrics as BaseCognitiveLoadMetrics } from '@birhaus/types'

export interface CognitiveLoadMetrics extends BaseCognitiveLoadMetrics {
  // Additional tools-specific metrics for detailed analysis
  
  // Navigation and choice analysis (required by CognitiveLoadMeter)
  navigationItems: number
  choiceOptions: number
  
  // Visual complexity analysis
  colorCount: number
  fontVariations: number
  visualLayers: number
  
  // Information density analysis
  textDensity: number
  actionDensity: number
  interactionComplexity: number
  
  // Accessibility analysis
  contrastIssues: number
  missingAltText: number
  keyboardNavigation: boolean
  
  // Spanish-first compliance analysis
  spanishLabelCoverage: number
  bilingualSupport: boolean
  
  // BIRHAUS compliance analysis
  undoPatternUsage: number
  confirmationDialogs: number
  progressiveDisclosure: number
}

export interface CognitiveLoadAnalysis {
  score: number // 0-100 (100 = perfect cognitive load)
  level: 'low' | 'medium' | 'high' | 'critical'
  violations: CognitiveViolation[]
  suggestions: CognitiveSuggestion[]
  metrics: CognitiveLoadMetrics
}

export interface CognitiveViolation {
  id: string
  type: 'miller-law' | 'visual-complexity' | 'accessibility' | 'birhaus-principle'
  severity: 'low' | 'medium' | 'high' | 'critical'
  messageEs: string
  messageEn?: string
  element?: Element
  birhausPrinciple?: number
}

export interface CognitiveSuggestion {
  id: string
  priority: 'low' | 'medium' | 'high'
  actionEs: string
  actionEn?: string
  estimatedImpact: number // 0-100
  birhausPrinciple?: number
}

export interface CognitiveLoadMeterProps {
  // Target element to analyze
  _targetSelector?: string
  targetElement?: Element
  
  // Analysis configuration
  enableRealTimeAnalysis?: boolean
  analysisInterval?: number
  
  // Display options
  position?: 'top-right' | 'bottom-right' | 'bottom-left' | 'top-left' | 'floating'
  minimized?: boolean
  showDetails?: boolean
  
  // Filtering
  showOnlyViolations?: boolean
  minSeverity?: 'low' | 'medium' | 'high' | 'critical'
  
  // Callbacks
  onAnalysisComplete?: (analysis: CognitiveLoadAnalysis) => void
  onViolationDetected?: (violation: CognitiveViolation) => void
  
  // Styling
  className?: string
}

// eslint-disable-next-line max-lines-per-function -- CognitiveLoadMeter requires complex analysis and UI rendering logic
export function CognitiveLoadMeter({
  _targetSelector = 'body', // Not used in current implementation
  targetElement,
  enableRealTimeAnalysis = true,
  analysisInterval = 2000,
  position = 'top-right',
  minimized: initialMinimized = false,
  showDetails = true,
  showOnlyViolations = false,
  minSeverity = 'medium',
  onAnalysisComplete,
  onViolationDetected,
  className
}: CognitiveLoadMeterProps) {
  
  // State management
  const [analysis, setAnalysis] = useState<CognitiveLoadAnalysis | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [minimized, setMinimized] = useState(initialMinimized)
  const [showSettings, setShowSettings] = useState(false)
  
  const analysisRef = useRef<NodeJS.Timeout>()
  
  // BIRHAUS: Analyze cognitive load of target element
  // eslint-disable-next-line max-lines-per-function, complexity -- Analysis requires comprehensive DOM traversal and metric calculation
  const analyzeCognitiveLoad = (): CognitiveLoadAnalysis => {
    const target = targetElement || document.querySelector(_targetSelector || 'body') || document.body
    const metrics: CognitiveLoadMetrics = {
      // Base CognitiveLoadMetrics properties
      visibleChoices: 0,
      primaryNavItems: 0,
      contextualActions: 0,
      primaryActions: 0,
      formFields: 0,
      tableColumns: 0,
      violatesMillersLaw: false,
      violatesFourThreeOne: false,
      cognitiveLoadScore: 0,
      violations: [],
      
      // Extended tools-specific properties
      navigationItems: 0,
      choiceOptions: 0,
      colorCount: 0,
      fontVariations: 0,
      visualLayers: 0,
      textDensity: 0,
      actionDensity: 0,
      interactionComplexity: 0,
      contrastIssues: 0,
      missingAltText: 0,
      keyboardNavigation: true,
      spanishLabelCoverage: 0,
      bilingualSupport: false,
      undoPatternUsage: 0,
      confirmationDialogs: 0,
      progressiveDisclosure: 0
    }
    
    const violations: CognitiveViolation[] = []
    const suggestions: CognitiveSuggestion[] = []
    
    // Miller's Law Analysis
    const navItems = target.querySelectorAll('nav a, nav button, [role="navigation"] a, [role="navigation"] button')
    metrics.navigationItems = navItems.length
    
    if (navItems.length > 7) {
      violations.push({
        id: 'nav-miller-law',
        type: 'miller-law',
        severity: navItems.length > 10 ? 'critical' : 'high',
        messageEs: `Navegación con ${navItems.length} elementos (máximo recomendado: 7)`,
        messageEn: `Navigation has ${navItems.length} items (recommended max: 7)`,
        birhausPrinciple: 1
      })
      
      suggestions.push({
        id: 'nav-reduce-items',
        priority: 'high',
        actionEs: 'Agrupa elementos de navegación o usa menús desplegables',
        actionEn: 'Group navigation items or use dropdown menus',
        estimatedImpact: 85,
        birhausPrinciple: 1
      })
    }
    
    // Form complexity analysis
    const formFields = target.querySelectorAll('input, select, textarea')
    metrics.formFields = formFields.length
    
    if (formFields.length > 7) {
      violations.push({
        id: 'form-miller-law',
        type: 'miller-law',
        severity: formFields.length > 10 ? 'high' : 'medium',
        messageEs: `Formulario con ${formFields.length} campos (considera dividir en pasos)`,
        messageEn: `Form has ${formFields.length} fields (consider multi-step)`,
        birhausPrinciple: 4
      })
      
      suggestions.push({
        id: 'form-progressive',
        priority: 'medium',
        actionEs: 'Usa MinimalForm para dividir en pasos de máximo 7 campos',
        actionEn: 'Use MinimalForm to split into steps of max 7 fields',
        estimatedImpact: 75,
        birhausPrinciple: 4
      })
    }
    
    // Choice options analysis
    const selects = target.querySelectorAll('select')
    selects.forEach((select: HTMLSelectElement) => {
      const options = select.querySelectorAll('option').length
      metrics.choiceOptions = Math.max(metrics.choiceOptions, options)
      
      if (options > 7) {
        violations.push({
          id: `select-miller-law-${Math.random()}`,
          type: 'miller-law',
          severity: options > 12 ? 'high' : 'medium',
          messageEs: `Lista desplegable con ${options} opciones (máximo recomendado: 7)`,
          messageEn: `Dropdown has ${options} options (recommended max: 7)`,
          element: select,
          birhausPrinciple: 1
        })
      }
    })
    
    // Visual complexity analysis
    const computedStyles = new Set<string>()
    const elements = target.querySelectorAll('*')
    
    elements.forEach((el: Element) => {
      const style = getComputedStyle(el)
      computedStyles.add(style.color)
      computedStyles.add(style.backgroundColor)
      computedStyles.add(style.fontFamily)
      computedStyles.add(style.fontSize)
    })
    
    metrics.colorCount = Array.from(computedStyles)
      .filter(value => value.includes('rgb') || value.includes('#')).length
    metrics.fontVariations = Array.from(computedStyles)
      .filter(value => value.includes('px') || value.includes('rem')).length
    
    // Action density analysis
    const clickableElements = target.querySelectorAll('button, a, [onclick], [role="button"]')
    metrics.actionDensity = clickableElements.length
    
    if (clickableElements.length > 12) {
      violations.push({
        id: 'action-density',
        type: 'visual-complexity',
        severity: 'medium',
        messageEs: `Demasiadas acciones disponibles (${clickableElements.length})`,
        messageEn: `Too many available actions (${clickableElements.length})`,
        birhausPrinciple: 3
      })
    }
    
    // Spanish-first compliance
    const textElements = target.querySelectorAll('[aria-label], label, button, a, h1, h2, h3, h4, h5, h6')
    let spanishElements = 0
    let bilingualElements = 0
    
    textElements.forEach((el: Element) => {
      const text = el.textContent || el.getAttribute('aria-label') || ''
      const hasSpanishPattern = /[ñáéíóúü]|[Ññ]|ción|dad|mente/i.test(text)
      const hasLabelEs = el.hasAttribute('data-label-es') || el.hasAttribute('labelEs')
      const hasLabelEn = el.hasAttribute('data-label-en') || el.hasAttribute('labelEn')
      
      if (hasSpanishPattern) spanishElements++
      if (hasLabelEs && hasLabelEn) bilingualElements++
    })
    
    metrics.spanishLabelCoverage = textElements.length > 0 
      ? (spanishElements / textElements.length) * 100 
      : 100
    metrics.bilingualSupport = bilingualElements > 0
    
    if (metrics.spanishLabelCoverage < 70) {
      violations.push({
        id: 'spanish-first',
        type: 'birhaus-principle',
        severity: 'medium',
        messageEs: `Solo ${Math.round(metrics.spanishLabelCoverage)}% de elementos usan español`,
        messageEn: `Only ${Math.round(metrics.spanishLabelCoverage)}% of elements use Spanish`,
        birhausPrinciple: 7
      })
      
      suggestions.push({
        id: 'add-spanish-labels',
        priority: 'medium',
        actionEs: 'Agrega propiedades labelEs a los componentes',
        actionEn: 'Add labelEs properties to components',
        estimatedImpact: 60,
        birhausPrinciple: 7
      })
    }
    
    // Confirmation dialog detection (anti-pattern)
    const confirmationTexts = ['¿está seguro?', 'confirmar', 'are you sure?', 'confirm']
    const confirmationElements = Array.from(target.querySelectorAll('*'))
      .filter((el: Element) => confirmationTexts.some(text => 
        el.textContent?.toLowerCase().includes(text)
      ))
    
    metrics.confirmationDialogs = confirmationElements.length
    
    if (confirmationElements.length > 0) {
      violations.push({
        id: 'confirmation-dialogs',
        type: 'birhaus-principle',
        severity: 'high',
        messageEs: `${confirmationElements.length} diálogo(s) de confirmación detectado(s)`,
        messageEn: `${confirmationElements.length} confirmation dialog(s) detected`,
        birhausPrinciple: 5
      })
      
      suggestions.push({
        id: 'replace-with-undo',
        priority: 'high',
        actionEs: 'Reemplaza confirmaciones con funcionalidad de deshacer',
        actionEn: 'Replace confirmations with undo functionality',
        estimatedImpact: 90,
        birhausPrinciple: 5
      })
    }
    
    // Accessibility analysis
    const imagesWithoutAlt = target.querySelectorAll('img:not([alt])')
    metrics.missingAltText = imagesWithoutAlt.length
    
    if (imagesWithoutAlt.length > 0) {
      violations.push({
        id: 'missing-alt-text',
        type: 'accessibility',
        severity: 'high',
        messageEs: `${imagesWithoutAlt.length} imagen(es) sin texto alternativo`,
        messageEn: `${imagesWithoutAlt.length} image(s) missing alt text`,
        birhausPrinciple: 6
      })
    }
    
    // Calculate overall score
    // violations.length can be used for future violation limit feature
    const weightedViolations = violations.reduce((acc, v) => {
      const weights = { low: 1, medium: 2, high: 4, critical: 8 }
      return acc + weights[v.severity]
    }, 0)
    
    const score = Math.max(0, 100 - (weightedViolations * 5))
    const level = score >= 80 ? 'low' : 
                 score >= 60 ? 'medium' : 
                 score >= 40 ? 'high' : 'critical'
    
    return {
      score,
      level,
      violations: showOnlyViolations ? violations : violations,
      suggestions: suggestions.slice(0, 5), // Top 5 suggestions
      metrics
    }
  }
  
  // Run analysis (memoized to prevent useEffect re-renders)
  const runAnalysis = useCallback(() => {
    setIsAnalyzing(true)
    
    setTimeout(() => {
      const result = analyzeCognitiveLoad()
      setAnalysis(result)
      setIsAnalyzing(false)
      
      onAnalysisComplete?.(result)
      
      // Report violations
      result.violations.forEach(violation => {
        if (shouldShowViolation(violation)) {
          onViolationDetected?.(violation)
        }
      })
    }, 100)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Function dependencies are complex, stable in practice
  }, [onAnalysisComplete, onViolationDetected])
  
  // Filter violations by severity
  const shouldShowViolation = (violation: CognitiveViolation) => {
    const severityLevels = { low: 1, medium: 2, high: 3, critical: 4 }
    const minLevel = severityLevels[minSeverity]
    const violationLevel = severityLevels[violation.severity]
    return violationLevel >= minLevel
  }
  
  // Real-time analysis setup
  useEffect(() => {
    if (enableRealTimeAnalysis) {
      runAnalysis() // Initial analysis
      
      analysisRef.current = setInterval(() => {
        runAnalysis()
      }, analysisInterval)
      
      return () => {
        if (analysisRef.current) {
          clearInterval(analysisRef.current)
        }
      }
    }
  }, [enableRealTimeAnalysis, analysisInterval, runAnalysis])
  
  const positionClasses = {
    'top-right': 'fixed top-4 right-4 z-50',
    'bottom-right': 'fixed bottom-4 right-4 z-50',
    'bottom-left': 'fixed bottom-4 left-4 z-50',
    'top-left': 'fixed top-4 left-4 z-50',
    'floating': 'fixed top-1/2 right-4 -translate-y-1/2 z-50'
  }
  
  const scoreColors = {
    low: 'text-green-600 bg-green-100',
    medium: 'text-yellow-600 bg-yellow-100',
    high: 'text-orange-600 bg-orange-100',
    critical: 'text-red-600 bg-red-100'
  }
  
  if (minimized) {
    return (
      <div className={cn(positionClasses[position], className)}>
        <button
          onClick={() => setMinimized(false)}
          className={cn(
            'flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-lg hover:shadow-xl transition-shadow',
            analysis && scoreColors[analysis.level]
          )}
        >
          <Brain className="h-4 w-4" />
          {analysis && (
            <span className="text-sm font-medium">{analysis.score}</span>
          )}
          <Maximize2 className="h-3 w-3" />
        </button>
      </div>
    )
  }
  
  return (
    <div className={cn(positionClasses[position], 'w-80 max-w-[calc(100vw-2rem)]', className)}>
      <div className="bg-white border border-gray-300 rounded-lg shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-gray-900">Cognitive Load</span>
            {isAnalyzing && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
            )}
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <Settings className="h-4 w-4" />
            </button>
            <button
              onClick={() => setMinimized(true)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <Minimize2 className="h-4 w-4" />
            </button>
            <button
              onClick={runAnalysis}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <BarChart3 className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        {/* Score */}
        {analysis && (
          <div className="p-4">
            <div className={cn(
              'flex items-center justify-between p-3 rounded-lg',
              scoreColors[analysis.level]
            )}>
              <div>
                <div className="text-2xl font-bold">{analysis.score}</div>
                <div className="text-sm capitalize">{analysis.level} load</div>
              </div>
              <div className="text-right text-sm">
                <div>{analysis.violations.length} violaciones</div>
                <div>{analysis.suggestions.length} sugerencias</div>
              </div>
            </div>
          </div>
        )}
        
        {/* Details */}
        {analysis && showDetails && (
          <div className="p-4 border-t border-gray-200">
            <div className="space-y-3">
              {/* Key metrics */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span>Navegación:</span>
                  <span className={analysis.metrics.navigationItems > 7 ? 'text-red-600' : 'text-green-600'}>
                    {analysis.metrics.navigationItems}/7
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Campos:</span>
                  <span className={analysis.metrics.formFields > 7 ? 'text-red-600' : 'text-green-600'}>
                    {analysis.metrics.formFields}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Español:</span>
                  <span className={analysis.metrics.spanishLabelCoverage < 70 ? 'text-yellow-600' : 'text-green-600'}>
                    {Math.round(analysis.metrics.spanishLabelCoverage)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Confirmaciones:</span>
                  <span className={analysis.metrics.confirmationDialogs > 0 ? 'text-red-600' : 'text-green-600'}>
                    {analysis.metrics.confirmationDialogs}
                  </span>
                </div>
              </div>
              
              {/* Top violations */}
              {analysis.violations.slice(0, 3).map((violation) => (
                <div key={violation.id} className="flex items-start gap-2 p-2 bg-gray-50 rounded text-xs">
                  <AlertTriangle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{violation.messageEs}</div>
                    {violation.birhausPrinciple && (
                      <div className="text-gray-500">Principio BIRHAUS #{violation.birhausPrinciple}</div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Top suggestions */}
              {analysis.suggestions.slice(0, 2).map((suggestion) => (
                <div key={suggestion.id} className="flex items-start gap-2 p-2 bg-blue-50 rounded text-xs">
                  <Zap className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{suggestion.actionEs}</div>
                    <div className="text-gray-500">Impacto estimado: {suggestion.estimatedImpact}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// BIRHAUS: Hook for using cognitive load analysis programmatically
export function useCognitiveLoadAnalysis(_targetSelector = 'body') { // Reserved for future DOM targeting
  const [analysis, setAnalysis] = useState<CognitiveLoadAnalysis | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  
  const runAnalysis = () => {
    // Implementation would be similar to the component's analysis logic
    // This is a simplified version for the hook
    setIsAnalyzing(true)
    
    setTimeout(() => {
      // Mock analysis for now - real implementation would mirror the component
      const mockAnalysis: CognitiveLoadAnalysis = {
        score: 85,
        level: 'low',
        violations: [],
        suggestions: [],
        metrics: {
          // Base CognitiveLoadMetrics properties
          visibleChoices: 5,
          primaryNavItems: 4,
          contextualActions: 3,
          primaryActions: 1,
          formFields: 3,
          tableColumns: 6,
          violatesMillersLaw: false,
          violatesFourThreeOne: false,
          cognitiveLoadScore: 85,
          violations: [],
          
          // Extended tools-specific properties
          navigationItems: 4,
          choiceOptions: 5,
          colorCount: 8,
          fontVariations: 3,
          visualLayers: 4,
          textDensity: 45,
          actionDensity: 6,
          interactionComplexity: 3,
          contrastIssues: 0,
          missingAltText: 0,
          keyboardNavigation: true,
          spanishLabelCoverage: 85,
          bilingualSupport: true,
          undoPatternUsage: 2,
          confirmationDialogs: 0,
          progressiveDisclosure: 3
        }
      }
      
      setAnalysis(mockAnalysis)
      setIsAnalyzing(false)
    }, 500)
  }
  
  return {
    analysis,
    isAnalyzing,
    runAnalysis
  }
}

// Components and utilities are exported via individual export declarations above